/**
 * WorkspaceNodeSearchService.js
 *
 * Workspace-scoped node search helpers:
 * - Firestore candidate retrieval (anchor token)
 * - Client-side token filtering and relevance scoring
 * - Type option aggregation for filter UI
 */

// Firebase
import { collection, getDocs, limit, query, where } from 'firebase/firestore';

// Config
import { collections, db } from 'firebaseConfig';

// Utilities
import { timestampToMillis } from 'utilities/DateTime';
import {
  indexMatchesAllTokens,
  normalizeSearchText,
  tokenizeSearchQuery,
} from 'services/search/SearchIndexService';
import { filterNodesByVisibility, NodeVisibility } from 'services/nodes/NodeTrash';

const DEFAULT_LIMIT = 80;
const DEFAULT_TYPE = 'ALL';
const DEFAULT_SORT = 'relevance';
const DEFAULT_TIME_WINDOW = 'any';

/**
 * Selects the longest token as the Firestore anchor token.
 */
const getAnchorToken = (tokens) => {
  if (!Array.isArray(tokens) || !tokens.length) return '';
  return tokens.slice().sort((a, b) => b.length - a.length)[0] || '';
};

/**
 * Returns best-available node timestamp in milliseconds.
 */
const getNodeTimeMs = (node) => {
  return timestampToMillis(node?.dateModified) || timestampToMillis(node?.dateCreated) || 0;
};

/**
 * Computes relevance score for a candidate row.
 */
const scoreNode = ({ node, tokens, rawQuery }) => {
  const title = String(node?.title || '').toLowerCase();
  const titleCompact = title.replace(/\s+/g, ' ').trim();
  const index = Array.isArray(node?.index) ? node.index : [];
  const indexSet = new Set(index);
  const queryText = normalizeSearchText(rawQuery);

  let score = 0;

  for (const token of tokens) {
    if (!token) continue;
    if (titleCompact === token) score += 140;
    if (titleCompact.startsWith(token)) score += 90;
    if (titleCompact.includes(token)) score += 40;
    if (indexSet.has(token)) score += 25;

    const wordRegex = new RegExp(`(^|[^a-z0-9])${token}([^a-z0-9]|$)`);
    if (wordRegex.test(titleCompact)) score += 35;
  }

  if (queryText && titleCompact.includes(queryText)) {
    score += 100;
  }

  if (String(node?.type || '').toUpperCase() === 'USER' && queryText.startsWith('@')) {
    score += 50;
  }

  const ageMs = Date.now() - getNodeTimeMs(node);
  if (ageMs > 0) {
    const ageDays = ageMs / (1000 * 60 * 60 * 24);
    score += Math.max(0, 24 - ageDays) * 1.25;
  }

  return score;
};

/**
 * Applies optional recency window filtering.
 */
const applyTimeFilter = (rows, timeWindow) => {
  if (!timeWindow || timeWindow === DEFAULT_TIME_WINDOW) return rows;

  const days = Number(timeWindow);
  if (!Number.isFinite(days) || days <= 0) return rows;

  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return rows.filter((row) => getNodeTimeMs(row) >= cutoff);
};

/**
 * Sorts rows by selected sort mode.
 */
const sortRows = (rows, sortBy) => {
  if (String(sortBy || DEFAULT_SORT) === 'recent') {
    return rows.slice().sort((a, b) => getNodeTimeMs(b) - getNodeTimeMs(a));
  }

  return rows
    .slice()
    .sort((a, b) => {
      if (b.searchScore !== a.searchScore) return b.searchScore - a.searchScore;
      return getNodeTimeMs(b) - getNodeTimeMs(a);
    });
};

/**
 * searchWorkspaceNodes
 *
 * Runs workspace-scoped search using Firestore for candidate retrieval and
 * client-side token filtering + ranking for relevance.
 *
 * @param {Object} params
 * @param {string} params.rawQuery
 * @param {string} params.workspaceKey
 * @param {string} [params.type='ALL']
 * @param {'relevance'|'recent'} [params.sortBy='relevance']
 * @param {'any'|'7'|'30'|'365'|string} [params.timeWindow='any']
 * @param {number} [params.max=80]
 *
 * @returns {Promise<Object[]>}
 */
export async function searchWorkspaceNodes({
  rawQuery,
  workspaceKey,
  type = DEFAULT_TYPE,
  sortBy = DEFAULT_SORT,
  timeWindow = DEFAULT_TIME_WINDOW,
  max = DEFAULT_LIMIT,
  visibility = NodeVisibility.ACTIVE,
} = {}) {
  if (!workspaceKey) return [];

  const tokens = tokenizeSearchQuery(rawQuery);
  if (!tokens.length) return [];

  const anchorToken = getAnchorToken(tokens);
  if (!anchorToken) return [];

  const constraints = [
    where('workspaceKey', '==', workspaceKey),
    where('index', 'array-contains', anchorToken),
    limit(Math.max(1, Number(max) || DEFAULT_LIMIT)),
  ];

  const normalizedType = String(type || DEFAULT_TYPE).trim();
  if (normalizedType && normalizedType.toUpperCase() !== DEFAULT_TYPE) {
    constraints.splice(1, 0, where('type', '==', normalizedType));
  }

  const snapshot = await getDocs(query(collection(db, collections.nodes), ...constraints));

  const candidateRows = snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    key: docSnap.id,
    ...(docSnap.data() || {}),
  }));

  const visibleCandidates = filterNodesByVisibility(candidateRows, visibility);

  const matchesAllTokens = visibleCandidates.filter((row) => {
    return indexMatchesAllTokens(row?.index, tokens);
  });

  const scoredRows = matchesAllTokens.map((row) => ({
    ...row,
    searchScore: scoreNode({ node: row, tokens, rawQuery }),
  }));

  const timeFiltered = applyTimeFilter(scoredRows, timeWindow);
  return sortRows(timeFiltered, sortBy);
}

/**
 * Builds type options from search rows.
 *
 * Returns canonical type keys with counts, grouped case-insensitively.
 *
 * @param {Object[]} rows
 * @returns {Array<{ typeKey: string, count: number }>}
 */
export function getSearchTypeOptions(rows = []) {
  const counts = new Map();

  rows.forEach((row) => {
    const rawTypeKey = String(row?.type || 'NODE').trim() || 'NODE';
    const bucketKey = rawTypeKey.toLowerCase();
    const prev = counts.get(bucketKey);
    if (prev) {
      prev.count += 1;
      return;
    }

    counts.set(bucketKey, {
      typeKey: rawTypeKey,
      count: 1,
    });
  });

  return Array.from(counts.values())
    .sort((a, b) => b.count - a.count || a.typeKey.localeCompare(b.typeKey));
}

/**
 * Normalizes URL query text.
 */
export function normalizeSearchQuery(value) {
  return normalizeSearchText(value);
}
